/**
 * OnboardingChecklist — first-time-user setup nudge
 *
 * Three-step checklist that lives at the top of /dashboard until ALL three
 * are complete, at which point it self-removes (returns null). Driven by
 * three independent backend signals:
 *
 *   step 1 — Connect AWS
 *           ✓ when GET /api/idle-killer/cloud-connect returns a
 *             readonly_role_arn
 *   step 2 — Connect LLM proxy
 *           ✓ when GET /api/llm-proxy/keys returns at least one item
 *   step 3 — Review your first savings opportunity
 *           ✓ when POST /api/idle-killer/scan returns >=1 candidate AND
 *             the user has visited /idle-killer (we just check candidate>0
 *             since the visit isn't tracked)
 *
 * Honest UX choice: dismissible. If a user wants to hide it, they can.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import InlineAWSConnect from './InlineAWSConnect';
import { CheckCircle2, Circle, X, Cloud, Sparkles, Eye } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const DISMISS_KEY = 'cartie_onboarding_dismissed';

const STEPS = [
  {
    key: 'aws',
    title: 'Connect your AWS account',
    subtitle: 'Read-only first. Takes ~5 min.',
    href: '/idle-killer/connect',
    icon: Cloud,
  },
  {
    key: 'llm',
    title: 'Mint a proxy key for AI savings',
    subtitle: 'Drop in once, save 30-60% on token spend.',
    href: '/llm-proxy',
    icon: Sparkles,
  },
  {
    key: 'review',
    title: 'Review your first savings opportunity',
    subtitle: 'See exactly what CARTIE found before approving anything.',
    href: '/idle-killer',
    icon: Eye,
  },
];

export default function OnboardingChecklist() {
  const [status, setStatus] = useState({ aws: false, llm: false, review: false });
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === '1');
  const [loading, setLoading] = useState(true);
  const [awsModalOpen, setAwsModalOpen] = useState(false);  // v35.20 — inline AWS connect

  useEffect(() => {
    if (dismissed) { setLoading(false); return; }
    (async () => {
      try {
        const [cc, keys, scan] = await Promise.all([
          axios.get(`${API}/idle-killer/cloud-connect`, auth()).catch(() => ({ data: {} })),
          axios.get(`${API}/llm-proxy/keys`, auth()).catch(() => ({ data: {} })),
          axios.post(`${API}/idle-killer/scan`, {}, auth()).catch(() => ({ data: {} })),
        ]);
        setStatus({
          aws:    !!cc.data?.readonly_role_arn,
          llm:    (keys.data?.items || []).length > 0,
          review: (scan.data?.candidate_count || 0) > 0,
        });
      } finally { setLoading(false); }
    })();
  }, [dismissed]);

  const allDone = status.aws && status.llm && status.review;
  if (dismissed || loading || allDone) return null;

  const completedCount = [status.aws, status.llm, status.review].filter(Boolean).length;

  return (
    <div className="rounded-2xl bg-slate-900 ring-1 ring-emerald-500/20 p-5 mb-6" data-testid="onboarding-checklist">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-sm font-semibold text-slate-100 flex items-center gap-2">
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500/15 ring-1 ring-emerald-500/30 text-emerald-300 text-[10px] font-bold">{completedCount}/3</span>
            Set up CARTIE in 3 steps
          </h3>
          <p className="text-xs text-slate-400 mt-1">Connect once, save on cloud + AI every month after.</p>
        </div>
        <button
          onClick={() => { localStorage.setItem(DISMISS_KEY, '1'); setDismissed(true); }}
          className="text-slate-500 hover:text-slate-300 p-1"
          aria-label="Dismiss onboarding"
          data-testid="onboarding-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="space-y-2">
        {STEPS.map((s, i) => {
          const done = status[s.key];
          const Icon = s.icon;
          // v35.20 — AWS step opens inline modal; others remain navigation links
          const isInlineAws = s.key === 'aws' && !done;
          const className = `block rounded-lg p-3 ring-1 transition-colors text-left w-full ${done ? 'bg-slate-950 ring-emerald-500/15 opacity-60' : 'bg-slate-950 ring-white/10 hover:ring-emerald-500/30 hover:bg-slate-950/80'}`;
          const body = (
              <div className="flex items-center gap-3">
                {done
                  ? <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
                  : <Circle className="w-5 h-5 text-slate-600 shrink-0" />
                }
                <Icon className={`w-4 h-4 shrink-0 ${done ? 'text-slate-500' : 'text-emerald-400'}`} />
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-semibold ${done ? 'text-slate-500 line-through' : 'text-slate-100'}`}>
                    {i + 1}. {isInlineAws ? 'Connect AWS in 30 seconds' : s.title}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {isInlineAws ? 'Inline IAM wizard — no leaving this page.' : s.subtitle}
                  </div>
                </div>
                {!done && (
                  <span className="text-xs text-emerald-400 font-mono">{isInlineAws ? 'Open →' : 'Start →'}</span>
                )}
              </div>
          );
          return isInlineAws ? (
            <button
              key={s.key}
              type="button"
              className={className}
              onClick={() => setAwsModalOpen(true)}
              data-testid={`onboarding-step-${s.key}`}
            >
              {body}
            </button>
          ) : (
            <Link
              key={s.key}
              to={s.href}
              className={className}
              data-testid={`onboarding-step-${s.key}`}
            >
              {body}
            </Link>
          );
        })}
      </div>
      <InlineAWSConnect
        open={awsModalOpen}
        onClose={() => setAwsModalOpen(false)}
        onConnected={() => {
          setStatus((s) => ({ ...s, aws: true }));
          setAwsModalOpen(false);
        }}
      />
    </div>
  );
}
